/**
 * CourseList — trainee-facing course catalogue
 *
 * Shows only the courses assigned to the current trainee via
 * cert_trainee_courses. Each card carries a status badge:
 * Not Started / In Progress / Complete / Please Redo (content updated).
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import EmptyState from '../../EmptyState';
import { getCourseCompletion, type CourseCompletionStatus } from '../../../lib/training';

interface Course {
  id: string;
  name: string;
  description?: string;
  type: string;
  sort_order?: number;
  cert_training_course_modules?: { module_id: string }[];
}

interface CourseListProps {
  onSelectCourse: (courseId: string) => void;
  accentColor?: string;
}

const TYPE_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  ongoing: 'Ongoing',
  refresher: 'Refresher',
};

const STATUS_CONFIG: Record<CourseCompletionStatus, { label: string; bg: string; color: string }> = {
  not_started: { label: 'Not Started', bg: 'var(--color-bg)', color: 'var(--color-text-light)' },
  in_progress: { label: 'In Progress', bg: '#fef9c3', color: '#ca8a04' },
  complete: { label: 'Complete', bg: 'var(--color-success-bg)', color: 'var(--color-success)' },
  stale: { label: 'Please Redo', bg: 'var(--color-error-bg)', color: 'var(--color-error)' },
};

function CourseList({ onSelectCourse, accentColor = '#772cbb' }: CourseListProps) {
  const { supabase, user } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [statuses, setStatuses] = useState<Record<string, CourseCompletionStatus>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCourses();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per user change
  }, [user?.id]);

  async function loadCourses() {
    if (!user?.id) {
      setCourses([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    // Only the courses assigned to this trainee
    const { data: assignments } = await supabase
      .from('cert_trainee_courses')
      .select('course_id')
      .eq('user_id', user.id);

    const courseIds = (assignments || []).map((a: { course_id: string }) => a.course_id);

    if (courseIds.length === 0) {
      setCourses([]);
      setStatuses({});
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from('cert_training_courses')
      .select('*, cert_training_course_modules(module_id)')
      .in('id', courseIds)
      .eq('active', true)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });

    const loadedCourses = (data as Course[]) || [];
    setCourses(loadedCourses);

    // Fetch completion status for each course
    const statusEntries = await Promise.all(
      loadedCourses.map(async (c) => {
        const result = await getCourseCompletion(supabase, user.id, c.id);
        return [c.id, result?.status ?? 'not_started'] as const;
      }),
    );
    setStatuses(Object.fromEntries(statusEntries));
    setLoading(false);
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-light)' }}>
        Loading your training...
      </div>
    );
  }

  if (courses.length === 0) {
    // Two paths reach this state:
    // - Signed-in trainee with no course assignments → "ask your manager"
    // - Anonymous visitor (no user) → they followed a share link that
    //   wasn't pointing at a specific course. Direct them back.
    const isAnonymous = !user?.id;
    return (
      <EmptyState
        icon="🎓"
        message={isAnonymous ? 'Open the course link your manager sent you' : 'Your training has not been set up yet'}
        hint={isAnonymous
          ? 'The course opens directly from the link in your message. If you don’t have one, please ask your property manager to resend it.'
          : 'Please contact your training manager.'}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem' }}>
      {courses.map(course => {
        const moduleCount = course.cert_training_course_modules?.length || 0;
        const status = statuses[course.id] ?? 'not_started';
        const statusStyle = STATUS_CONFIG[status];

        return (
          <div
            key={course.id}
            onClick={() => onSelectCourse(course.id)}
            style={{
              background: 'var(--color-white)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.5rem',
              cursor: 'pointer',
              border: '1px solid #e5e7eb',
              transition: 'box-shadow 0.2s, transform 0.2s',
              position: 'relative',
              overflow: 'hidden',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'none';
            }}
          >
            <div className="flex items-start justify-between mb-md gap-sm">
              <div style={{
                display: 'inline-block',
                padding: '3px 10px',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                background: `${accentColor}15`,
                color: accentColor,
              }}>
                {TYPE_LABELS[course.type] || course.type}
              </div>

              <div style={{
                display: 'inline-block',
                padding: '3px 10px',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                background: statusStyle.bg,
                color: statusStyle.color,
              }}>
                {statusStyle.label}
              </div>
            </div>

            <h3 style={{ margin: '0 0 0.5rem', fontSize: 'var(--text-2xl)', fontWeight: 600 }}>
              {course.name}
            </h3>

            {course.description && (
              <p style={{ margin: '0 0 1rem', fontSize: 'var(--text-base)', color: 'var(--color-text-light)', lineHeight: 1.5 }}>
                {course.description.length > 120
                  ? course.description.substring(0, 120) + '...'
                  : course.description}
              </p>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: '0.75rem',
              borderTop: '1px solid #f3f4f6',
            }}>
              <span style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>
                {moduleCount} module{moduleCount !== 1 ? 's' : ''}
              </span>
              <span style={{
                fontSize: 'var(--text-md)',
                fontWeight: 500,
                color: accentColor,
              }}>
                {status === 'complete' ? 'Review' : status === 'stale' ? 'Redo' : 'Start'} &rarr;
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default CourseList;
